// What the Toolkits surface reads: discovery on this person's connection —
// what the workspace offers, with each toolkit's tools and setup — the
// person's own toolkit paths, which say linked or configured without
// revealing a value, and the workloads of the install's MCP runtime image
// that are not serving yet, so that a toolkit that is starting with no
// tools is shown as such (02-DESIGN/08-toolkits/02-the-mcp-runtime.md §
// lifecycle).

import { identity } from "@impire-io/sdk";

import { CODE_NO_SERVICE, isRefusal } from "../../platform/bus.ts";
import { usePage } from "../../platform/session.tsx";
import { useLoad, type Load } from "../../ui/components.tsx";
import { personToolkitPrefix, type Toolkit } from "../../wire/toolkit.ts";
import {
  listSubject,
  PlatformName,
  RegistrationState,
  SERVICE as WORKLOADS,
  type ListReply,
  type Workload,
} from "../../wire/workloads.ts";
import { useSurface } from "../surface.tsx";
import { platformWorkload } from "../workloads/state.ts";

/** Offered is one row of the page: a toolkit discovery answered, or a runtime workload not serving yet. */
export interface Offered {
  name: string;
  toolkit?: Toolkit;
  /** The runtime's registration when it is not discovered: starting, stopped, failing. */
  workload?: Workload;
}

export interface Toolkits {
  offered: Load<Offered[]>;
  toolkits: Load<Toolkit[]>;
  /** The person's own toolkit paths; empty where the workspace has no vault yet. */
  linked: Load<Set<string>>;
  /** Whether the workspace has a vault, without which nothing can be linked. */
  noVault: boolean;
  /** The workspace's vault workload: null where it is not among the workloads, undefined where they cannot be read. */
  vault: Workload | null | undefined;
  /** The install's MCP runtime image, with which an admin adds a toolkit; absent when the install publishes none. */
  image: string | undefined;
  reload: () => void;
}

export function useToolkits(): Toolkits {
  const s = useSurface();
  const { platform } = usePage();
  // The install's MCP runtime image, from the platform profile (decision
  // 0017); absent where the operator set none, and the add form says so.
  const image = platform.images?.[identity.Image.toolkitMCP] || undefined;
  const [toolkits, reloadToolkits] = useLoad("toolkits", () => s.bus.discoverToolkits());
  const principal = s.principal;
  const [paths, reloadPaths] = useLoad(`toolkits/linked/${principal}`, () =>
    principal === ""
      ? Promise.resolve(new Set<string>())
      : s.bus
          .vault()
          .list(personToolkitPrefix(principal))
          .then((reply) => new Set(reply.paths.map((e) => e.path))),
  );
  const [workloads, reloadWorkloads] = useLoad("toolkits/workloads", () =>
    s.bus.call<ListReply>(WORKLOADS, listSubject(), undefined),
  );

  const noVault = paths.state === "refused" && isRefusal(paths.err, CODE_NO_SERVICE);
  const linked: Load<Set<string>> = noVault ? { state: "ready", value: new Set<string>() } : paths;
  const vault =
    workloads.state === "ready"
      ? (platformWorkload(workloads.value.workloads, PlatformName.vault) ?? null)
      : undefined;

  let offered: Load<Offered[]>;
  if (toolkits.state !== "ready") {
    offered = toolkits;
  } else {
    const rows: Offered[] = toolkits.value.map((tk) => ({ name: tk.name, toolkit: tk }));
    const serving = new Set(toolkits.value.map((tk) => tk.workload));
    if (workloads.state === "ready" && image !== undefined) {
      for (const w of workloads.value.workloads) {
        if (w.image === image && w.state !== RegistrationState.retired && !serving.has(w.id)) {
          rows.push({ name: w.name, workload: w });
        }
      }
    }
    rows.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    offered = { state: "ready", value: rows };
  }

  return {
    offered,
    toolkits,
    linked,
    noVault,
    vault,
    image,
    reload: () => {
      reloadToolkits();
      reloadPaths();
      reloadWorkloads();
    },
  };
}
